import { useCallback, useState } from 'react';
import { FetchError } from 'api/API';
import { A30, A30Input, A30Output } from 'api/U002';

type AuditionTermsRegisterDoRequest = (param: {
  request: A30Input;
  onSuccess?: (response: A30Output) => void;
  onError?: (err: Error) => void;
  cancellation?: AbortSignal;
}) => Promise<void>;

export function useAuditionTermsRegister(): [
  AuditionTermsRegisterDoRequest,
  boolean,
  Error | null,
] {
  const [loading, setLoading] = useState<boolean>(false);
  const [err, setError] = useState<Error | null>(null);

  const auditionTermsRegister = async (request: A30Input, cancellation: AbortSignal) => {
    const json = await A30(request, cancellation);
    return json;
  };

  const doRequest = useCallback<AuditionTermsRegisterDoRequest>(
    async ({ request, cancellation, onSuccess, onError }) => {
      const controller = new AbortController();
      const cancelSignal = cancellation ?? controller.signal;
      try {
        setLoading(true);
        setError(null);
        const result = await auditionTermsRegister(request, cancelSignal);
        onSuccess?.(result);
      } catch (err) {
        console.error({ err });
        if (err instanceof FetchError) {
          const errorData = await err.response.json();
          const flooredError = new Error(errorData.message || `HTTP ERROR: ${err.message}`);
          onError?.(flooredError);
          setError(flooredError);
        } else {
          const flooredError = new Error(`unknown error. ${err}`);
          onError?.(flooredError);
          setError(flooredError);
        }
      } finally {
        setLoading(false);
      }
    },
    [auditionTermsRegister, setLoading, setError]
  );

  return [doRequest, loading, err];
}
